// Smart Daara - Class Management Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { courseAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Plus, Pencil, Trash2, GraduationCap, Loader2, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClassManagement() {
  const { user } = useAuth();
  const { language } = useLanguage();
  
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    name_fr: '',
    description: '',
    description_fr: '',
    order: 1
  });

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const response = await courseAPI.listClasses({ tenant_id: user?.tenant_id });
      setClasses(response.data || []);
    } catch (error) {
      console.error('Error loading classes:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (classItem = null) => {
    if (classItem) {
      setEditingClass(classItem);
      setFormData({
        name: classItem.name || '',
        name_fr: classItem.name_fr || '',
        description: classItem.description || '',
        description_fr: classItem.description_fr || '',
        order: classItem.order || 1
      });
    } else {
      setEditingClass(null);
      setFormData({
        name: '',
        name_fr: '',
        description: '',
        description_fr: '',
        order: classes.length + 1
      });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name_fr) {
      toast.error(language === 'fr' ? 'Le nom est requis' : 'Name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingClass) {
        await courseAPI.updateClass(editingClass.id, formData);
        toast.success(language === 'fr' ? 'Classe mise à jour!' : 'Class updated!');
      } else {
        await courseAPI.createClass({
          ...formData,
          tenant_id: user?.tenant_id
        });
        toast.success(language === 'fr' ? 'Classe créée!' : 'Class created!');
      }
      setModalOpen(false);
      loadClasses();
    } catch (error) {
      console.error('Error saving class:', error);
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (classItem) => {
    if (!window.confirm(language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer cette classe?' 
      : 'Are you sure you want to delete this class?')) {
      return;
    }

    try {
      await courseAPI.deleteClass(classItem.id);
      toast.success(language === 'fr' ? 'Classe supprimée!' : 'Class deleted!');
      loadClasses();
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="class-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {language === 'fr' ? 'Gestion des Classes' : 'Class Management'}
          </h1>
          <p className="text-slate-500">
            {language === 'fr' 
              ? 'Configurez les niveaux/classes de votre établissement' 
              : 'Configure the levels/classes of your institution'}
          </p>
        </div>
        <Button 
          className="bg-emerald-700 hover:bg-emerald-800"
          onClick={() => openModal()}
          data-testid="add-class-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Nouvelle Classe' : 'New Class'}
        </Button>
      </div>

      {/* Classes Grid */}
      {classes.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <FolderOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {language === 'fr' ? 'Aucune classe configurée' : 'No classes configured'}
            </h3>
            <p className="text-slate-500 mb-4">
              {language === 'fr' 
                ? 'Commencez par créer vos classes (CE1, CE2, GEA1, IG1...)' 
                : 'Start by creating your classes (CE1, CE2, GEA1, IG1...)'}
            </p>
            <Button onClick={() => openModal()}>
              <Plus className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Créer une classe' : 'Create a class'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.sort((a, b) => a.order - b.order).map((classItem, index) => (
            <Card 
              key={classItem.id}
              className="border-0 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`class-card-${index}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openModal(classItem)}
                      data-testid={`edit-class-${index}`}
                    >
                      <Pencil className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(classItem)}
                      data-testid={`delete-class-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <h3 className="font-semibold text-lg text-slate-900 mb-1">
                  {classItem.name_fr || classItem.name}
                </h3>
                <p className="text-sm text-slate-500 line-clamp-2">
                  {classItem.description_fr || classItem.description || 
                    (language === 'fr' ? 'Aucune description' : 'No description')}
                </p>
                
                <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
                  <span className="text-slate-500">
                    {language === 'fr' ? 'Ordre' : 'Order'}: {classItem.order}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingClass 
                ? (language === 'fr' ? 'Modifier la Classe' : 'Edit Class')
                : (language === 'fr' ? 'Nouvelle Classe' : 'New Class')
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom (Français)' : 'Name (French)'} *</Label>
                <Input
                  value={formData.name_fr}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_fr: e.target.value }))}
                  placeholder="Ex: CE1, GEA1, IG2..."
                  data-testid="class-name-fr"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom (Anglais)' : 'Name (English)'}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Grade 1, Year 2..."
                  data-testid="class-name-en"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
              <Input
                value={formData.description_fr}
                onChange={(e) => setFormData(prev => ({ ...prev, description_fr: e.target.value }))}
                placeholder={language === 'fr' ? 'Description de la classe...' : 'Class description...'}
                data-testid="class-desc"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Ordre d\'affichage' : 'Display Order'}</Label>
              <Input
                type="number"
                min={1}
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 1 }))}
                data-testid="class-order"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-class-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Enregistrer' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
